import React from 'react';
import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import {
  Box,
  Button,
  CircularProgress,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import LocationComponent from '../../Stateless/Location';
import { UseCreateUser, UseEditUser } from '../../Query/UserQuery';
import PhoneNumberInput from '../Utils/PhoneNumberInput';
import ImageUploadField from '../Utils/ImageUploadField';
import { COLORS } from '../../../theme/themeConstants';
import { splitPhoneNumber } from '../../HelperFunctions/validationUtils';

const validationSchema = Yup.object().shape({
  name: Yup.string()
  .required('Please enter Customer Name')
  .matches(/^[A-Za-z0-9\s]+$/, 'Customer Name should only contain letters, numbers, and spaces')
  .max(100, 'Customer Name cannot exceed 100 characters')
  .test(
    'not-only-spaces',
    'Customer Name cannot be only spaces',
    (value) => {
      if (typeof value !== 'string') return false;
      return value.trim().length > 0;
    }
  ),

  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required')
    .test('is-empty', 'Email is required', function (value) {
      if (this.parent.isEdit) {
        return value && value.trim() !== '' ? true : false;
      }
      return true;
    }),
  phone: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    )
    .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
    alternativePhone: Yup.string()
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => !val || val?.length >= 9
    )
    .test(
      'not-same',
      'Alternate number must be different',
      function(val) {
        if (!val) return true;
        return val !== this.parent.phone;
      }
    ),
  customerAddress: Yup.string()
    .required('Please enter restaurant address')
    .test('is-empty', 'Address is required', function (value) {
      if (this.parent.isEdit) {
        return value && value.trim() !== '' ? true : false;
      }
      return true;
    }),

  latitude: Yup.string()
    .required('Latitude is required')
    .test('is-empty', 'Latitude is required', function (value) {
      if (this.parent.isEdit) {
        return value && value.trim() !== '' ? true : false;
      }
      return true;
    }),

  longitude: Yup.string()
    .required('Longitude is required')
    .test('is-empty', 'Longitude is required', function (value) {
      if (this.parent.isEdit) {
        return value && value.trim() !== '' ? true : false;
      }
      return true;
    }),

  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
      'Password must contain at least one uppercase letter, one number, and one special character'
    )
    .when('isEdit', {
      is: false,
      then: (schema) => schema.required('Please enter the password'),
    }),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), ''], 'Passwords must match')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
      'Password must contain at least one uppercase letter, one number, and one special character'
    )
    .when('isEdit', {
      is: false,
      then: (schema) => schema.required('Please enter the confirm password'),
    }),

  ProfileImage: Yup.mixed()
    .test('fileSize', 'Profile Image must be less than 500kb', (value: any) => {
      if (!value || typeof value === 'string') return true;
      return value.size <= 500000;
    })
    .test(
      'dimensions',
      'Profile Image must be 300x300px and above',
      (value: any): Promise<boolean> => {
        if (!value || !(value instanceof File)) return Promise.resolve(true);

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
              const { width, height } = img;
              if (
                width >= 300 &&
                height >= 300 &&
                width <= 500 &&
                height <= 500
              ) {
                resolve(true);
              } else {
                resolve(false);
              }
            };

            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value);
        });
      }
    )
    .test('is-empty', 'Profile image is required', function (value) {
      if (this.parent.isEdit) {
        return value ? true : false;
      }
      return true;
    })
    .required('Profile Image is required'),
});

type AddCustomerProps = {
  initialData?: {
    id: string;
    name: string;
    email: string;
    mobileNumber: string;
    alternativeMobileNumber: string;
    userAddress: {
      address: string;
      latitude: string;
      longitude: string;
    };
    profileImage: string;
  };
  onClose?: () => void;
};

// // Utility to split phone number into country code and phone
// const COUNTRY_CODES = [
//   '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'
// ];
// function splitPhoneNumber(fullNumber: string) {
//   if (!fullNumber) return { countryCode: '', phone: '' };
//   const code = COUNTRY_CODES.sort((a, b) => b.length - a.length)
//     .find(code => fullNumber.startsWith(code));
//   if (code) {
//     return {
//       countryCode: code,
//       phone: fullNumber.slice(code.length)
//     };
//   }
//   return { countryCode: '', phone: fullNumber };
// }


const AddCustomer: React.FC<AddCustomerProps> = ({ initialData, onClose }) => {
  const isEdit = !!initialData;
  const { mutate: CreateUser, isPending: CreateUserLoading } = UseCreateUser();
  const { mutate: EditUser, isPending: EditUserLoading } = UseEditUser();

  const { countryCode, phone } = splitPhoneNumber(initialData?.mobileNumber || '');
  const { countryCode: altCountryCode, phone: altPhone } = splitPhoneNumber(initialData?.alternativeMobileNumber || '');

  const AddUserForm = AllForm({
    // title: isEdit ? 'Edit Customer' : 'Add Customer',
    initialValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      countryCode: countryCode || '+962',
      phone: phone || '',
      alternativeCountryCode: altCountryCode || '+962',
      alternativePhone: altPhone || '',
      customerAddress: initialData?.userAddress?.address || '',
      latitude: initialData?.userAddress?.latitude || '',
      longitude: initialData?.userAddress?.longitude || '',
      ProfileImage: initialData?.profileImage || null,
      password: '',
      confirmPassword: '',
      isEdit,
    },
    validationSchema,
    onSubmit: async (values: any) => {
      const modifiedValues = {
        name: values.name,
        email: values.email,
        mobileNumber: `${values.countryCode}${values.phone}`,
        alternativeMobileNumber: `${values.alternativeCountryCode}${values.alternativePhone}`,
        userAddress: {
          address: values.customerAddress || '',
          latitude: values.latitude || '',
          longitude: values.longitude || '',
        },
        profileImage: values.ProfileImage,
        ...(values.password ? { password: values.password } : {}),
      };

      if (isEdit && initialData?.id) {
        EditUser(
          {
            id: initialData.id,
            data: {
              ...modifiedValues,
              userAddress: {
                address: values.customerAddress,
                latitude: values.latitude,
                longitude: values.longitude,
              },
            },
          },
          {
            onSuccess: () => {
              onClose?.();
            },
            onError: (error: any) => {
              console.error('EditUser error:', error);
            },
          }
        );
      } else {
        CreateUser(modifiedValues, {
          onSuccess: () => {
            onClose?.();
          },
          onError: (error: any) => {
            console.error('CreateUser error:', error);
          },
        });
      }
    },

    fields: [
      {
        name: 'name',
        label: 'Customer Name:',
        type: 'text',
        isRequired: true,
      },
      {
        name: 'email',
        label: 'Customer Email:',
        type: 'text',
        isRequired: true,
      },
      {
        name: 'password',
        label: 'Password:',
        type: 'password',
        isRequired: true,
      },
      {
        name: 'confirmPassword',
        label: 'Confirm Password:',
        type: 'password',
        isRequired: true,
      },
      {
        name: 'phone',
        label: 'Customer Phone:',
        type: 'custom',
        isRequired: true,
        component: ({ field, form }) => (
          <PhoneNumberInput
            field={field}
            form={form}
            selectedCountryCode={form.values.countryCode}
            onCountryCodeChange={code => form.setFieldValue('countryCode', code)}
          />
        )
      },
      {
        name: 'alternativePhone',
        label: 'Alternative Phone:',
        type: 'custom',
        isRequired: false,
        component: ({ field, form }) => (
          <PhoneNumberInput
            field={field}
            form={form}
            selectedCountryCode={form.values.alternativeCountryCode}
            onCountryCodeChange={code => form.setFieldValue('alternativeCountryCode', code)}
          />
        )
      },
      {
        name: 'customerAddress',
        label: 'Customer Address:',
        type: 'custom',
        isRequired: true,
        component: LocationComponent,
      },

      {
        name: 'ProfileImage',
        label: 'Profile Image:',
        type: 'file',
        isRequired: true,
        component: () => (
          <ImageUploadField
            name="ProfileImage"
            label="logo"
          />
        )
      },
    ],
    customButton: (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            backgroundColor: COLORS.primary.main,
            '&:hover': {
              backgroundColor: COLORS.primary.dark,
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {/* {isEdit ? 'Update' : 'Create'} */}
          {CreateUserLoading || EditUserLoading ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              {CreateUserLoading ? 'Updating...' : 'Creating...'}
            </>
          ) : isEdit ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
        {/* <Button
          type="button"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            backgroundColor: COLORS.primary.main,
            '&:hover': {
              backgroundColor: alpha(COLORS.primary.main, 0.8),
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
          // onClick={() => formikProps.handleSubmit()}
          disabled={CreateUserLoading || EditUserLoading}
        >
          {(CreateUserLoading || EditUserLoading) ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              {initialData ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            initialData ? 'Update' : 'Create'
          )}
        </Button> */}

        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            borderColor: COLORS.secondary.main,
            color: COLORS.secondary.main,
            '&:hover': {
              borderColor: COLORS.secondary.main,
              backgroundColor: 'rgba(76, 10, 39, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    ),
  });
  return (
    <>
      {/* <Backdrop
        open={CreateUserLoading || EditUserLoading}
        sx={{ zIndex: 1300 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop> */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '@media (max-width: 600px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
          },
        }}
      >
        {/* <Typography variant="h6" component="div" sx={{ color: 'gray' }}>
          {isEdit ? 'Edit Customer' : 'Add Customer'}
        </Typography>
        {isEdit ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              '@media (max-width: 600px)': {
                position: 'absolute',
                right: 8,
                top: 8,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null} */}
      </DialogTitle>
      <DialogContent>
        <AddUserForm />
      </DialogContent>
    </>
  );
};

export default AddCustomer;
